import { Box, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import styles from "../ContractSearch.styles";
import { fill } from "../ContractSearchHelper";
import { AiTurn } from "../ContractSearchTypes";
import AnswerMarkdown from "./AnswerMarkdown";
import { InfoGlyph } from "./ContractSearchIcons";

interface AnswerCardProps {
  turn: AiTurn;
  /** Indexed documents — used in the "No contracts matched" wording. */
  totalIndexed: number;
}

/**
 * The white card holding the AI's text. Four shapes, one component:
 * answer (with an optional "answered from X of Y" badge), refusal (guardrail),
 * empty (nothing matched) and error (the call failed).
 */
const AnswerCard = ({ turn, totalIndexed }: AnswerCardProps) => {
  const copy = strings.contractSearch;
  const partial =
    turn.kind === "answer" &&
    turn.matchedCount > 0 &&
    turn.usedCount < turn.matchedCount;

  let body = turn.answer;
  if (turn.kind === "empty" && !body) {
    body = fill(copy.emptyAnswer, { count: totalIndexed });
  }
  if (turn.kind === "error") {
    body = turn.answer || copy.errorAnswer;
  }

  const isRefusal = turn.kind === "refusal";

  return (
    <Box sx={styles.card(isRefusal || turn.kind === "error")} role="group">
      {partial && (
        <Box sx={styles.partialBadge}>
          <InfoGlyph sx={{ width: 12, height: 12, flexShrink: 0 }} />
          {fill(copy.partialBadge, {
            used: turn.usedCount,
            matched: turn.matchedCount,
          })}
        </Box>
      )}
      {isRefusal && (
        <Box sx={styles.cardTitleRow}>
          <InfoGlyph sx={{ width: 17, height: 17, flexShrink: 0 }} />
          <span>{copy.refusalTitle}</span>
        </Box>
      )}
      {turn.kind !== "error" ? (
        // Every AI reply is light Markdown (per-contract headings, bold runs) -- including the
        // conversational ones (lists, counts, clarifying questions) that cite no passage.
        <AnswerMarkdown answer={body} />
      ) : (
        <Typography component="p" sx={styles.answerBody}>
          {body}
        </Typography>
      )}
      {partial && (
        <Typography component="p" sx={styles.partialNote}>
          {fill(copy.partialNote, { rest: turn.matchedCount - turn.usedCount })}
        </Typography>
      )}
    </Box>
  );
};

export default AnswerCard;
